import styles from './NewReport.module.scss'
import { useState, useEffect } from 'react'
import Loader from 'components/miscellaneous/Loader/Loader';
import DateRangeSelector from 'components/miscellaneous/DateRangeSelector/DateRangeSelector';
import moment from 'moment';
import api from 'helpers/api';
import { useDispatch } from 'react-redux';
import { getUserClinic } from 'store/auth-slice';
import { getClinics } from 'store/data-slice';
import { useHistory } from 'react-router-dom';
import types from '../../Types/ReportTypes';
import MultiSelect from 'components/miscellaneous/MultiSelect/MultiSelect';

const NewReport = ({ visible, userType }) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const [ currentStep, setCurrentStep ] = useState(0);
    const [ selectedType, setSelectedType ] = useState(0);
    const [ settings, setSettings ] = useState({
        date_range: {
            startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD'),
        }
    });
    const [ errors, setErrors ] = useState([]);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isInitiated, setIsInitiated ] = useState(false);
    const [ smartOptions, setSmartOptions ] = useState({
        clinics: [],
        chains: [],
    });

    useEffect(() => {
        if(errors.length > 0) {
            Object.keys(settings).forEach(key => {
                setErrors(errors.filter(error => error.field.id !== key));
            })
        }
    }, [settings])


    useEffect(() => {
        if (visible) {
            if (isInitiated) {
                reset();
            } else {
                setIsInitiated(true);
            }
        }
    }, [ visible ])

    useEffect(() => {
        setIsLoading(true);
        dispatch(getClinics(true)).then((res) => {            
            const clinics = [{ value: '', label: 'No clinic selected' }, ...res.filter((c => c.is_chain === false)).map((clinic) => {
                return {
                    value: clinic.id,
                    label: clinic.name
                }
            })]

            const chains = [{ value: '', label: 'No chain selected' }, ...res.filter((c => c.is_chain === true)).map((clinic) => {
                return {
                    value: clinic.id,
                    label: clinic.name
                }
            })]

            setSmartOptions({
                ...smartOptions,
                clinics: clinics,
                chains: chains
            });

            setIsLoading(false);
        });

    }, [])

    const steps = [
        {
            title: 'Create a new report',
            description: 'Choose report type & settings to generate a new report',
            content: () => {
                return (
                    <div className={ styles.reportTypes }>
                        { types.map((type, index) => {
                            let permitted = true;

                            if (type.minUserRole !== undefined) {
                                permitted = userType >= type.minUserRole;
                            } else if (type.userRole !== undefined) {
                                if(Array.isArray(type.userRole)) {
                                    permitted = type.userRole.includes(userType);
                                } else {
                                    permitted = userType === type.userRole;
                                }
                            }

                            return (permitted &&
                                <div key={ index } onClick={ (e) => {
                                    setSelectedType(index);
                                } } className={ `${styles.reportType} ${selectedType === index && styles.reportTypeSelected}` }>
                                    { type.icon }
                                    <div>
                                        <div className={ styles.reportTypeTitle }>{ type.title }</div>
                                        <div className={ styles.reportTypeDescription }>{ type.description }</div>
                                    </div>
                                </div>
                            )
                        }
                        ) }
                    </div>
                )
            }
        },
        {
            title: `Create new ${types[ selectedType ].title} report`,
            description: 'Choose report type & settings to generate a new report',
            content: () => {
                const fields = types[ selectedType ].fields;
                return (
                    <div className={ styles.fields }>
                        { fields.map((field, index) => {
                            let permitted = true;

                            if (field.minUserRole !== undefined) {
                                permitted = userType >= field.minUserRole;
                            } else if (field.userRole !== undefined) {
                                permitted = userType === field.userRole;
                            }

                            const options = field.type === 'select' ? (
                                Array.isArray(field.options) ? field.options : smartOptions[ field.options ]
                            ) : [];

                            return (permitted &&
                                <div key={ index }>
                                    <div className={ styles.fieldTitle }>{ field.label }</div>
                                    <div className={ styles.fieldContent }>
                                        { field.type === 'date_range' && <DateRangeSelector greyStyle={ true } fullWidth={ true } onChange={ (data) => {
                                            setSettings({ ...settings, [ field.id ]: data });
                                        } } /> }
                                    
                                        { field.type === 'select' && Array.isArray(options) && <MultiSelect
                                            searchable
                                            options={options}
                                            selected={options.find((option) => option.id === settings[ field.id ])}
                                            placeholder={field.placeholder}
                                            onValueChange={(data) => {
                                                setSettings({ ...settings, [ field.id ]: data.value });
                                            }} /> }
                                    </div>
                                    <div className={ styles.fieldError }>
                                        { getFieldError(field) ? <div>{getFieldError(field).msg}</div> : '' }
                                    </div>
                                </div>
                            )
                        }) }
                    </div>
                )
            }
        }
    ]

    const createReport = () => {
        const validated = validate();
        if(validated === true) {
            setIsLoading(true);

            api.post('/report', {
                type: types[ selectedType ].id,
                filters: settings
            }).then((res) => {
                if (res.success) {
                    const data = res.data.data;
                    history.push(`/report/${data.token}`);
                } else {
                    // Request failed
                    const error = res.response.data.errors;
                    console.error(error)
                }
            }).catch((err) => {
    
            }).finally(() => {
                setIsLoading(false);
            });
        } else {
            setErrors(validated);
        }
    }

    const validate = () => {
        const fields = types[ selectedType ].fields;
        const errors = [];

        fields.forEach((field) => {
            let permitted = true;

            if (field.minUserRole !== undefined) {
                permitted = userType >= field.minUserRole;
            } else if (field.userRole !== undefined) {
                permitted = userType === field.userRole;
            }

            if(permitted) {
                if (field.required && !settings[ field.id ]) {
                    errors.push({
                        field: field,
                        reason: 'required',
                        msg: 'This field is required'
                    });
                }
            }
        })

        return errors.length > 0 ? errors : true;
    }

    const getFieldError = (field) => {
        return errors.find((error) => error.field.id === field.id);
    }


    const reset = () => {
        setCurrentStep(0);
        setSelectedType(0);
        setSettings({
            date_range: {
                startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
                endDate: moment().format('YYYY-MM-DD'),
            }
        });
        setIsLoading(false);
        setErrors([]);
    }


    const activeStep = steps[ currentStep ];

    return (
        <div className={ styles.modalContent }>
            <div className={ `${styles.modalLoader} ${isLoading && styles.modalLoaderActive}` }>
                <Loader />
            </div>
            <div className={ styles.modalHeader }>
                <h3>{ activeStep.title }</h3>
                <p>{ activeStep.description }</p>
            </div>
            <div>
                { activeStep.content() }
            </div>
            <div className={ styles.modalFooter }>
                <button disabled={ currentStep === 0 } onClick={ (e) => {
                    setCurrentStep(currentStep - 1);
                } } className={ styles.modalFooterButton }>Go Back</button>
                <button className={ styles.continueButton } onClick={ (e) => {
                    if (currentStep !== steps.length - 1) {
                        setCurrentStep(currentStep + 1);
                    } else {
                        // Create report
                        createReport();
                    }
                } }>
                    { currentStep === steps.length - 1 ? 'Create Report' : 'Continue' }
                </button>
            </div>
        </div>
    )

}

export default NewReport